import { CommonUtils } from '../(common-lib)/CommonUtils';
import { LoanData } from '../(common-lib)/LoanData';
import { getDownloadFileName } from '../(common-lib)/EmailTemplates';
/**
 * Service for handling document generation and download functionality
 */
export class DocumentService {

  // TODO-Aravind-P3: Remove client download related code once
  // the server is stable.
  private static readonly CLIENT_DOWNLOAD = false;

  /**
   * Downloads a document by fetching HTML content from the API and converting it to PDF
   * @param {Object} loanData - The form data to send to the API
   * @param {string} documentType - Type of document to generate ('pq-letter' or 'loan-details')
   * @param {Function} [onLoadingChange] - Optional callback for loading state changes
   * @return {Promise<void>} Promise that resolves when download completes
   */
  static async downloadDocument(loanData: any, documentType: string): Promise<void> {
    if (this.CLIENT_DOWNLOAD) {
      await this.clientDownloadDocument(loanData, documentType);
    } else {
      await this.serverDownloadDocument(loanData, documentType);
    }
  }

  private static async clientDownloadDocument(loanData: any, documentType: string): Promise<void> {
    try {
      const element = await this.fetchAndProcessHtml(loanData, documentType);
      await this.generateAndDownloadPdf(element, documentType, loanData);
    } catch (error) {
      CommonUtils.logError(`Failed to download ${documentType}:`, error);
      alert('Failed to download. Please try again.');
    }
  }

  private static async serverDownloadDocument(loanData: any, documentType: string): Promise<void> {
    let blobUrl: string | null = null;
    try {
      const pdfBlob = await this.getPdfBlobFromServer(loanData, documentType);
      const filename = getDownloadFileName(documentType,loanData);
      blobUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();  
    } catch (error) {
      CommonUtils.logError(`Failed to download ${documentType}:`, error);
      alert('Failed to download. Please try again.');
    } finally {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    }
}

  /**
   * Generates a document as Blob for programmatic handling
   * @param {Object} loanData - The form data to send to the API
   * @param {string} documentType - Type of document to generate ('pq-letter' or 'loan-details')
   * @return {Promise<Blob>} Promise that resolves with PDF as Blob
   */
  static async generateDocumentBlob(
    loanData: any,
    documentType: string
  ): Promise<Blob> {
    if (this.CLIENT_DOWNLOAD) {
      return await this.clientGenerateDocumentBlob(loanData, documentType);
    } else {
      return await this.serverGenerateDocumentBlob(loanData, documentType);
    }
  }

  static async clientGenerateDocumentBlob(
    loanData: any,
    documentType: string
  ): Promise<Blob> {
    const element = await this.fetchAndProcessHtml(loanData, documentType);
    return this.generatePdfBlob(element, documentType);
  }

  static async serverGenerateDocumentBlob(
    loanData: any,
    documentType: string
  ): Promise<Blob> {
    return await this.getPdfBlobFromServer(loanData, documentType);
  }

  /**
   * Triggers a browser download for a given Blob object.
   * @param {Blob} blob - The Blob object to download.
   * @param {string} filename - The desired filename for the download.
   */
  static downloadBlob(blob: Blob, filename: string): void {
    let blobUrl: string | null = null;
    try {
      blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove(); // Clean up the DOM
    } catch (error) {
      CommonUtils.logError(`Failed to download blob as ${filename}:`, error);
      alert('Failed to download. Please try again.');
    } finally {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl); // Clean up the object URL
      }
    }
  }

  /**
   * Fetches HTML content from API and processes it for PDF conversion
   * @private
   * @param {Object} loanData - The form data to send to the API
   * @param {string} documentType - Type of document to generate
   * @return {Promise<HTMLElement>} Processed HTML element ready for conversion
   * @throws {Error} When API request fails
   */
  private static async fetchAndProcessHtml(
    loanData: any, 
    documentType: string
  ): Promise<HTMLElement> {
    const res = await fetch('/api/document', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...loanData, documentType }),
    });

    if (!res.ok) throw new Error('Failed to generate document');

    const htmlContent: string = await res.text();
    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    
    await this.convertImagesToDataURL(element);
    
    return element;
  }

  private static async getPdfBlobFromServer(
    loanData: any, 
    documentType: string
  ): Promise<Blob> {
    const startTime = performance.now();
    const res = await fetch('/api/to_pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...loanData, documentType }),
    });
    CommonUtils.logInfo(`PDF API call took ${(performance.now() - startTime).toFixed(2)}ms`);

    if (!res.ok) throw new Error('Failed to generate document');
    const pdfBlob: Blob = await res.blob();
    return pdfBlob;
  }

  /**
   * Generates PDF and triggers browser download
   * @private
   * @param {HTMLElement} element - Processed HTML element to convert
   * @param {string} documentType - Type of document being generated
   * @return {Promise<void>} Promise that resolves when PDF is saved
   */
  private static async generateAndDownloadPdf(
    element: HTMLElement, 
    documentType: string,
    loanData:LoanData
  ): Promise<void> {
    const options = this.getPdfOptions(documentType);
    const pdfLib = await import('html2pdf.js');
    pdfLib.default()
      .set({ ...options, filename: getDownloadFileName(documentType,loanData) })
      .from(element)
      .save();
  }

  /**
   * Generates PDF as Blob object
   * @private
   * @param {HTMLElement} element - Processed HTML element to convert
   * @param {string} documentType - Type of document being generated
   * @return {Promise<Blob>} PDF as Blob object
   */
  private static async generatePdfBlob(
    element: HTMLElement,
    documentType: string
  ): Promise<Blob> {
    const options = this.getPdfOptions(documentType);
    const pdfLib = await import('html2pdf.js');
    return pdfLib.default()
      .set(options)
      .from(element)
      .outputPdf('blob');
  }

  /**
   * Gets PDF generation options based on document type
   * @private
   * @param {string} documentType - Type of document being generated
   * @return {Object} Configuration options for PDF generation
   */
  private static getPdfOptions(documentType: string): object {
    return {
      margin: documentType === 'pq-letter' ? [15, 20, 15, 20] : [5, 20, 5, 20],
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
  }

  /**
   * Converts all images in element to data URLs
   * @private
   * @param {HTMLElement} element - HTML element containing images
   * @return {Promise<void>} Promise that resolves when all images are converted
   */
  private static async convertImagesToDataURL(element: HTMLElement): Promise<void> {
    await Promise.all(Array.from(element.querySelectorAll('img')).map(
      async (img: HTMLImageElement) => {
        const src = img.getAttribute('src');
        if (src && !src.startsWith('data:')) {
          try {
            img.src = await this.convertImageToDataURL(img);
          } catch (error) {
            CommonUtils.logError(`Failed to load image: ${src}`, error);
          }
        }
      }
    ));
  }

  /**
   * Converts single image to data URL
   * @private
   * @param {HTMLImageElement} img - Image element to convert
   * @return {Promise<string>} Data URL representation of the image
   */
  private static async convertImageToDataURL(img: HTMLImageElement): Promise<string> {
    const response = await fetch(img.src);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  }

}